import {
  LIMITS,
  parseKeepLabelsFile,
  parseKeepNote,
  resolveAttachmentPath,
  type ParsedKeepNote,
} from '@keep/shared';
import type { ArchiveEntry } from './archive';

export interface PlannedAttachment {
  entry: ArchiveEntry;
  name: string;
  mimeType: string | null;
}

export interface PlannedNote {
  note: ParsedKeepNote['note'];
  /** Nom du fichier source, pour les messages. */
  source: string;
  attachments: PlannedAttachment[];
}

export interface ImportPlan {
  notes: PlannedNote[];
  labels: string[];
  warnings: Array<{ source: string; message: string }>;
  stats: {
    notes: number;
    checklists: number;
    pinned: number;
    archived: number;
    trashed: number;
    images: number;
  };
}

/** Un JSON de note Keep pèse quelques Ko : au-delà, ce n'est pas une note (autre produit Takeout). */
const MAX_NOTE_JSON_BYTES = 2 * 1024 * 1024;

const dirname = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');
const basename = (path: string) => path.slice(path.lastIndexOf('/') + 1);

/**
 * Analyse un export Google Takeout : trouve les notes Keep (où qu'elles soient dans l'archive),
 * leurs images et les libellés. Rien n'est envoyé au serveur à cette étape.
 */
export async function planKeepImport(
  entries: ArchiveEntry[],
  onProgress?: (done: number, total: number) => void,
): Promise<ImportPlan> {
  const byDirectory = new Map<string, Map<string, ArchiveEntry>>();
  for (const entry of entries) {
    const dir = dirname(entry.path);
    if (!byDirectory.has(dir)) byDirectory.set(dir, new Map());
    byDirectory.get(dir)!.set(basename(entry.path), entry);
  }

  const candidates = entries.filter(
    (e) => /\.json$/i.test(e.path) && e.size <= MAX_NOTE_JSON_BYTES,
  );
  const labelFiles = entries.filter((e) => basename(e.path).toLowerCase() === 'labels.txt');
  const decoder = new TextDecoder();
  const plan: ImportPlan = {
    notes: [],
    labels: [],
    warnings: [],
    stats: { notes: 0, checklists: 0, pinned: 0, archived: 0, trashed: 0, images: 0 },
  };
  const usedIds = new Set<string>();

  for (const [index, entry] of candidates.entries()) {
    onProgress?.(index, candidates.length);
    let json: unknown;
    try {
      json = JSON.parse(decoder.decode(await entry.read()));
    } catch {
      continue; // fichier JSON d'un autre produit ou illisible
    }
    const source = basename(entry.path);
    const parsed = parseKeepNote(json, source);
    if (!parsed) continue;

    // Deux notes créées à la même microseconde : on garantit tout de même l'unicité.
    let externalId = parsed.note.externalId;
    for (let n = 2; usedIds.has(externalId); n++) externalId = `${parsed.note.externalId}#${n}`;
    usedIds.add(externalId);

    const siblings = byDirectory.get(dirname(entry.path)) ?? new Map<string, ArchiveEntry>();
    const attachments: PlannedAttachment[] = [];
    for (const ref of parsed.attachments) {
      const name = resolveAttachmentPath(ref.filePath, siblings.keys());
      const file = name ? siblings.get(name) : undefined;
      if (!file) {
        plan.warnings.push({
          source,
          message: `Image introuvable dans l’archive : ${ref.filePath}`,
        });
      } else if (file.size > LIMITS.attachmentBytes) {
        plan.warnings.push({ source, message: `Image trop volumineuse ignorée : ${ref.filePath}` });
      } else {
        attachments.push({ entry: file, name: name!, mimeType: ref.mimeType });
      }
    }
    for (const message of parsed.warnings) plan.warnings.push({ source, message });

    plan.notes.push({ note: { ...parsed.note, externalId }, source, attachments });
    const { stats } = plan;
    stats.notes++;
    if (parsed.note.type === 'checklist') stats.checklists++;
    if (parsed.note.pinned) stats.pinned++;
    if (parsed.note.archived) stats.archived++;
    if (parsed.note.trashed) stats.trashed++;
    stats.images += attachments.length;
  }
  onProgress?.(candidates.length, candidates.length);

  const labels = new Map<string, string>();
  for (const file of labelFiles) {
    for (const name of parseKeepLabelsFile(decoder.decode(await file.read()))) {
      labels.set(name.toLocaleLowerCase(), name);
    }
  }
  for (const { note } of plan.notes) {
    for (const name of note.labels)
      if (!labels.has(name.toLocaleLowerCase())) labels.set(name.toLocaleLowerCase(), name);
  }
  plan.labels = [...labels.values()].sort((a, b) => a.localeCompare(b, 'fr'));

  // Envoyées de la plus ancienne à la plus récente : la dernière modifiée apparaîtra en haut de la grille.
  plan.notes.sort((a, b) => (a.note.updatedAt ?? '').localeCompare(b.note.updatedAt ?? ''));
  return plan;
}
